import { useInAppNotificationSocketContext } from '@/contexts/inAppNotification';
import { useDataQuery } from '@/hooks/shared/useDataQuery';
import { useInAppNotificationsAvailable } from '@/hooks/inAppNotification/useInAppNotificationsAvailable';
import { fetchInAppNotifications } from '@/queries/inAppNotification';
import { QUERY_KEYS } from '@/resources/dataKeys';
import type { InAppNotificationsData } from '@/types/inAppNotification';

export const IN_APP_NOTIFICATIONS_QUERY_KEY = [QUERY_KEYS.inAppNotifications];

/**
 * Polling interval used only while the socket is not delivering.
 *
 * With a healthy socket the badge is kept current by live events plus a backfill
 * on every rejoin, so no polling is needed. If the socket gives up entirely the
 * badge would otherwise be frozen for the rest of the session.
 */
const DEGRADED_REFETCH_INTERVAL_MS = 3 * 60 * 1000;

export const useInAppNotifications = () => {
  const isAvailable = useInAppNotificationsAvailable();
  const { status } = useInAppNotificationSocketContext();

  const { data, ...rest } = useDataQuery<InAppNotificationsData>(
    IN_APP_NOTIFICATIONS_QUERY_KEY,
    fetchInAppNotifications,
    {
      enabled: isAvailable,
      refetchOnWindowFocus: true,
      refetchInterval: status === 'joined' ? false : DEGRADED_REFETCH_INTERVAL_MS,
    },
  );

  return {
    notifications: data?.notifications ?? [],
    totalUnseen: data?.totalUnseen ?? 0,
    ...rest,
  };
};
